import type { ApiClient } from '@carbonwebmail/api-client'
import { useAuth } from '@clerk/react'
import { Link, useNavigate, useParams } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { AuthLayout } from '../../ui/AuthLayout.js'
import { PillButton } from '../../ui/PillButton.js'
import { useJoin } from './useJoin.js'

/**
 * Accepting an invitation.
 *
 * The organisation and the address are shown before anything is asked for.
 * Somebody arriving from an unexpected email needs to see what they are being
 * asked to join before they sign in anywhere, and an invitation is exactly the
 * shape of a phishing message.
 *
 * **There is no form.** The address comes from the invitation, the role from
 * whoever sent it, and the name from the identity provider — so there is
 * nothing on this page for the holder of a link to choose. That is what stops
 * a forwarded invitation becoming an account at an address of somebody else's
 * choosing inside another organisation.
 *
 * Signing in comes first, and happens at the provider. Whoever is already
 * signed in sees one button; whoever is not is sent to sign in and returned
 * here, because the invitation cannot be accepted by an anonymous caller any
 * more — which is strictly more confinement than the single-use token alone.
 */
export function JoinPage({
  client,
  onJoined,
}: {
  readonly client: ApiClient
  /** Re-reads the session, so the app learns there is an organisation now. */
  readonly onJoined: () => Promise<void>
}) {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const { isLoaded, isSignedIn } = useAuth()
  const { state, problem, joining, join } = useJoin({ client, token })

  return (
    <AuthLayout>
      <Async
        state={state}
        skeleton={<p className="text-[14px] text-t2">Checking that invitation…</p>}
        onRetry={() => window.location.reload()}
      >
        {(invitation) =>
          invitation === undefined ? (
            <>
              <h1 className="mb-1.5 font-bold text-[28px] text-t1 -tracking-[0.01em]">
                That link has expired
              </h1>
              <p className="mb-7 text-[14px] text-t2">
                Invitations last seven days and can only be used once. Ask whoever invited you to
                send another.
              </p>
              <Link className="font-semibold text-[14px] text-brand underline" to="/signin">
                Sign in instead
              </Link>
            </>
          ) : (
            <>
              <h1 className="mb-1.5 font-bold text-[28px] text-t1 -tracking-[0.01em]">
                Join {invitation.organizationName}
              </h1>
              <p className="mb-7 text-[14px] text-t2">
                You were invited as {invitation.email}.{' '}
                {isSignedIn
                  ? 'Accept and you are in.'
                  : 'Sign in with that address to accept — we will bring you straight back.'}
              </p>

              {problem === undefined ? null : <Alert>{problem}</Alert>}

              {isSignedIn ? (
                <PillButton
                  type="button"
                  variant="primary"
                  disabled={joining}
                  onClick={() => {
                    void join().then(async (joined) => {
                      if (!joined) return
                      // The same order as signing in, and for the same reason:
                      // the app must learn it has an organisation before this
                      // navigates, or the route it lands on sends it back.
                      await onJoined()
                      navigate('/', { replace: true })
                    })
                  }}
                >
                  {joining ? 'Joining…' : `Join ${invitation.organizationName}`}
                </PillButton>
              ) : (
                <PillButton
                  type="button"
                  variant="primary"
                  disabled={!isLoaded}
                  onClick={() => {
                    // Back to this exact link afterwards, so accepting is one
                    // more click rather than a second hunt through the inbox.
                    navigate(`/signin?redirect_url=${encodeURIComponent(window.location.pathname)}`)
                  }}
                >
                  Sign in to accept
                </PillButton>
              )}
            </>
          )
        }
      </Async>
    </AuthLayout>
  )
}
